'use client'
import Container from '@mui/material/Container'
import { CustomSection, FormFieldType, LanguageCode, LocalStorageFormData, LocalStorageLocaleFormData } from '@/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FORM_DATA_LOCAL_STORAGE_KEY } from '@/constants/keys'
import { useLocale, useTranslations } from 'next-intl'
import confetti from 'canvas-confetti'
import dynamic from 'next/dynamic'
import { getLocalStorageData, setSelectedTemplateIdInLocalStorage } from '@/utils/StorageUtils'
import { WHITE } from '@/constants/colors'
import { generateWhatsAppMessage } from '@/utils/Utils'
import { useMbmLogger } from '@/hooks/useMbmLogger'
import { v4 as uuidv4 } from 'uuid'
import { Add } from '@mui/icons-material'
import { LANGUAGE_OPTIONS, RELIGION_DROPDOWN_OPTIONS } from '@/constants/options'
import { useSettings } from '@/providers/SettingsProvider'
import { SettingsItem, SupportedLanguage, SupportedReligion } from '@/enum'
import { useParams } from 'next/navigation'
import { usePathname, useRouter } from '@/i18n/routing'
import { withBasePath } from '@/constants/site'
import { PERSONAL_DETAILS_DATA } from '@/app/api/data/PersonalDetailsData'
import { FAMILY_DETAILS_DATA } from '@/app/api/data/FamilyDetailsData'
import { CONTACT_DETAILS_DATA } from '@/app/api/data/ContactDetailsData'
import FormSection from '@/app/components/Form/FormSection'
import InputTranslator from '@/app/components/shared/InputTranslator'

// Custom loading component that responds to screen size
const ResponsiveLoading = () => {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('md'))

  if (matches) {
    // Loading UI for screens smaller than 900px
    return (
      <Box sx={{ overflow: 'hidden', position: 'relative', height: '100%' }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            p: 2,
            boxShadow: 3,
            mb: 2
          }}>
          <Skeleton variant="rounded" width="100%" height={40} sx={{ padding: '9px 16px' }} />
          <Skeleton variant="rounded" width="100%" height={40} sx={{ padding: '9px 16px' }} />
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "space-between",
            p: 2
          }}>
          <Skeleton variant="rounded" width="47%" height={245} />
          <Skeleton variant="rounded" width="47%" height={245} />
          <Skeleton variant="rounded" width="47%" height={245} />
          <Skeleton variant="rounded" width="47%" height={245} />
          <Skeleton variant="rounded" width="47%" height={245} />
          <Skeleton variant="rounded" width="47%" height={245} />
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            p: 2,
            boxShadow: 3,
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            background: WHITE
          }}>
          <Skeleton variant="rounded" width="100%" height={40} sx={{ padding: '9px 16px' }} />
          <Skeleton variant="rounded" width="100%" height={40} sx={{ padding: '9px 16px' }} />
        </Stack>
      </Box>
    );
  } else {
    // Default loading UI for larger screens
    return (
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(256,256,256,0.2)',
        }}
        open={true}
      >
        <CircularProgress color="primary" />
      </Backdrop>
    )
  }
}

// Dynamic import with custom responsive loading component
const TemplateViewComponent = dynamic(() => import('@/app/components/TemplateView/TemplateView'), {
  loading: ResponsiveLoading,
})

const Home = () => {
  const lastProcessedSearch = useRef<string>('')
  const loadedLocaleRef = useRef<string | null>(null)
  const [personalDetails, setPersonalDetails] = useState<FormFieldType[]>([])
  const [familyDetails, setFamilyDetails] = useState<FormFieldType[]>([])
  const [contactDetails, setContactDetails] = useState<FormFieldType[]>([])
  const [customSections, setCustomSections] = useState<CustomSection[]>([])
  const [sectionTitles, setSectionTitles] = useState({
    personal: '',
    family: '',
    contact: '',
  })
  const [sectionVisibility, setSectionVisibility] = useState({
    personal: true,
    family: true,
    contact: true,
  })
  const [biodataMessage, setBiodataMessage] = useState<string>('')
  const [open, setOpen] = useState(false)
  const locale = useLocale()
  const [openTemplateView, setOpenTemplateView] = useState<boolean>(false)
  const params = useParams()
  const isWebView = params?.webview === 'true'
  const router = useRouter()
  const pathname = usePathname()
  const { settings, updateSetting, setShowProgress } = useSettings()
  const t = useTranslations('HOMEPAGE')
  const logger = useMbmLogger()

  // Initialize section titles
  useEffect(() => {
    const storedData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY)
    const storedTitles = storedData?.[locale as LanguageCode]?.sectionTitles
    const storedVisibility = storedData?.[locale as LanguageCode]?.sectionVisibility

    if (storedTitles?.personal && storedTitles?.family && storedTitles?.contact) {
      setSectionTitles({
        personal: storedTitles.personal,
        family: storedTitles.family,
        contact: storedTitles.contact,
      })
    } else {
      setSectionTitles({
        personal: t('PERSONAL_DETAILS_TITLE'),
        family: t('FAMILY_DETAILS_TITLE'),
        contact: t('CONTACT_DETAILS_TITLE'),
      })
    }

    if (storedVisibility) {
      setSectionVisibility(storedVisibility)
    }
  }, [t, locale])

  const handleModalClose = () => {
    setOpen(false)
  }

  const handleOpenTemplateView = () => {
    // show error if none of the sections are visible
    if (!sectionVisibility.personal && !sectionVisibility.family && !sectionVisibility.contact) {
      enqueueSnackbar(t('NO_SECTIONS_VISIBLE'), {
        variant: 'error',
        TransitionProps: { direction: 'up' },
      })
      return
    }

    // Check if visible sections have at least one non-hidden field
    const visibleSectionsWithoutFields = []
    if (sectionVisibility.personal && !personalDetails.some((field) => !field.isHidden)) {
      visibleSectionsWithoutFields.push(sectionTitles.personal)
    }
    if (sectionVisibility.family && !familyDetails.some((field) => !field.isHidden)) {
      visibleSectionsWithoutFields.push(sectionTitles.family)
    }
    if (sectionVisibility.contact && !contactDetails.some((field) => !field.isHidden)) {
      visibleSectionsWithoutFields.push(sectionTitles.contact)
    }

    if (visibleSectionsWithoutFields.length > 0) {
      enqueueSnackbar(t('SECTIONS_WITHOUT_FIELDS', { sections: visibleSectionsWithoutFields.join(', ') }), {
        variant: 'error',
        TransitionProps: { direction: 'up' },
      })
      return
    }

    setOpenTemplateView(true)
  }

  const handleCloseTemplateView = () => {
    setOpenTemplateView(false)
  }

  const generateBiodataMessage = () => {
    setBiodataMessage('')
    const { personal, family, contact } = sectionTitles
    const whatsAppMessage = generateWhatsAppMessage(
      personalDetails,
      familyDetails,
      contactDetails,
      personal,
      family,
      contact,
      customSections,
    )
    setBiodataMessage(whatsAppMessage)
    setOpen(true)
  }

  const copyClickHandler = async () => {
    await navigator.clipboard.writeText(biodataMessage || '')
    confetti({
      particleCount: 200,
      spread: 100,
      origin: { y: 0.75 },
      zIndex: 9999,
    })
    enqueueSnackbar(t('COPIED_TO_CLIPBOARD'), {
      variant: 'default',
      TransitionProps: { direction: 'up' },
    })
  }

  const addNewSection = () => {
    const newSection = {
      id: uuidv4(),
      title: `${t('NEW_SECTION_TITLE')} ${customSections.length + 1}`,
      fields: [],
    }
    setCustomSections((prev) => [...prev, newSection])
    logger.info('New section added', { section: newSection })
  }

  const handleSectionVisibilityToggle = (section: 'personal' | 'family' | 'contact') => {
    setSectionVisibility((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
    logger.info('Section visibility toggled', { section, visibility: sectionVisibility[section] })
  }

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register(withBasePath('/service-worker.js'))
        .then((registration) => {
          // Service Worker registered successfully
        })
        .catch((error) => {
          // Service Worker registration failed
        })
    }
  }, [])

  useEffect(() => {
    if (loadedLocaleRef.current === locale) {
      return
    }

    const fetchDetails = async () => {
      const storedData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY)
      let currentPersonalDetails: FormFieldType[] = []
      let currentFamilyDetails: FormFieldType[] = []
      let currentContactDetails: FormFieldType[] = []
      let currentCustomSections: CustomSection[] = []

      if (storedData?.[locale as LanguageCode]) {
        const { personalDetails: p, contactDetails: c, familyDetails: f, customSections: s } = storedData[locale as LanguageCode]
        if (p.length && c.length && f.length) {
          currentPersonalDetails = [...p]
          currentFamilyDetails = [...f]
          currentContactDetails = [...c]
          currentCustomSections = s || []
        }
      }

      if (currentPersonalDetails.length === 0) {
        // Fetch from API if local data is not valid or not present
        const fetchDataFromAPI = (): LocalStorageLocaleFormData => ({
          personalDetails: PERSONAL_DETAILS_DATA[locale as LanguageCode],
          familyDetails: FAMILY_DETAILS_DATA[locale as LanguageCode],
          contactDetails: CONTACT_DETAILS_DATA[locale as LanguageCode],
          customSections: [],
        })
        const initialData = fetchDataFromAPI()
        currentPersonalDetails = [...initialData.personalDetails]
        currentFamilyDetails = [...initialData.familyDetails]
        currentContactDetails = [...initialData.contactDetails]
        currentCustomSections = initialData.customSections
      }

      setPersonalDetails(currentPersonalDetails)
      setFamilyDetails(currentFamilyDetails)
      setContactDetails(currentContactDetails)
      setCustomSections(currentCustomSections)
      loadedLocaleRef.current = locale
    }

    fetchDetails()
  }, [locale, pathname, router, updateSetting])

  useEffect(() => {
    if (typeof window === 'undefined' || personalDetails.length === 0) {
      return
    }

    const currentSearch = window.location.search
    const searchParams = new URLSearchParams(currentSearch)
    const queryName = searchParams.get('name')
    const queryReligion = searchParams.get('religion')

    if (!queryName && !queryReligion) {
      return
    }

    if (currentSearch === lastProcessedSearch.current) {
      return
    }

    const pathParts = window.location.pathname.replace(/^\/create/, '').split('/').filter(Boolean)
    let urlLocale = 'en'
    if (pathParts[0] && Object.values(SupportedLanguage).includes(pathParts[0] as SupportedLanguage)) {
      urlLocale = pathParts[0]
    }

    // Partner links land on /create (English path) even when next-intl detects another locale.
    const canApplyQueryParams = locale === urlLocale || urlLocale === 'en'
    if (!canApplyQueryParams) {
      return
    }

    lastProcessedSearch.current = currentSearch

    let nextPersonalDetails = [...personalDetails]
    let shouldCleanUrl = false

    if (queryName) {
      nextPersonalDetails = nextPersonalDetails.map((field) =>
        field.identifier === 'name' ? { ...field, value: queryName } : field,
      )
      shouldCleanUrl = true
    }

    if (queryReligion) {
      const normalizedReligion = queryReligion.toLowerCase()
      if (Object.values(SupportedReligion).includes(normalizedReligion as SupportedReligion)) {
        updateSetting(SettingsItem.RELIGION, normalizedReligion)

        let hiddenFields: string[] = []
        if (normalizedReligion === SupportedReligion.Christianity) {
          hiddenFields = ['caste', 'gothra', 'rashi', 'nakshatra', 'mool']
        } else if (normalizedReligion === SupportedReligion.Buddhism) {
          hiddenFields = ['gothra', 'rashi', 'nakshatra', 'mool', 'kuldevat', 'gan', 'nadi', 'mangalik']
        } else if (normalizedReligion === SupportedReligion.Islam) {
          hiddenFields = ['gothra', 'rashi', 'nakshatra', 'mool']
        }

        nextPersonalDetails = nextPersonalDetails.map((field) => ({
          ...field,
          isHidden: hiddenFields.includes(field.identifier),
        }))
        shouldCleanUrl = true
      }
    }

    if (shouldCleanUrl) {
      setPersonalDetails(nextPersonalDetails)
      logger?.info('Connected experience applied', {
        name: queryName,
        religion: queryReligion,
        urlLocale,
        locale,
      })

      const newSearchParams = new URLSearchParams(currentSearch)
      newSearchParams.delete('name')
      newSearchParams.delete('religion')
      const newUrl = `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`
      router.replace(newUrl)
    }
  }, [locale, pathname, personalDetails, router, updateSetting, logger])

  useEffect(() => {
    const updateLocalStorage = (newFormData: LocalStorageFormData) => {
      localStorage.setItem(FORM_DATA_LOCAL_STORAGE_KEY, JSON.stringify(newFormData))
    }

    const debouncedUpdate = debounce((data: LocalStorageFormData) => {
      updateLocalStorage(data)
    }, 500)

    const storedData = localStorage.getItem(FORM_DATA_LOCAL_STORAGE_KEY)
    const formData = storedData ? JSON.parse(storedData) : {}

    const newFormDataForLang = {
      personalDetails,
      familyDetails,
      contactDetails,
      customSections,
      sectionTitles,
      sectionVisibility,
    }

    const updatedFormData = {
      ...formData,
      [locale]: newFormDataForLang,
    }

    debouncedUpdate(updatedFormData)
    return () => {
      // Clear the debounced function on cleanup
      debouncedUpdate.clear()
    }
  }, [personalDetails, familyDetails, contactDetails, customSections, sectionTitles, sectionVisibility, locale])

  // Handle template parameter from URL to auto-open template view
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const searchParams = new URLSearchParams(window.location.search)
    const templateId = searchParams.get('template')
    if (templateId) {
      setSelectedTemplateIdInLocalStorage(templateId)
      setOpenTemplateView(true)
      // Update the URL to remove the template parameter
      searchParams.delete('template')
      const newUrl = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
      router.replace(newUrl)
    }
  }, [pathname, router])

  const handleCustomSectionFieldsChange = useCallback(
    (sectionId: string) => (newFields: FormFieldType[] | ((prev: FormFieldType[]) => FormFieldType[])) => {
      setCustomSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, fields: typeof newFields === 'function' ? newFields(s.fields) : newFields } : s)),
      )
    },
    [],
  )

  const handleCustomSectionTitleChange = useCallback(
    (sectionId: string) => (newTitle: string) => {
      setCustomSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, title: newTitle } : s)))
      logger.info('Custom section title changed', { sectionId, newTitle })
    },
    [],
  )

  const handleCustomSectionDelete = useCallback(
    (sectionId: string) => () => {
      setCustomSections((prev) => prev.filter((s) => s.id !== sectionId))
      enqueueSnackbar(t('SECTION_DELETED'), {
        variant: 'default',
        TransitionProps: { direction: 'up' },
      })
      logger.info('Custom section deleted')
    },
    [enqueueSnackbar, t],
  )

  const handleLanguageChange = useCallback(
    (event: SelectChangeEvent) => {
      const nextLocale = event.target.value
      // @ts-expect-error -- TypeScript will validate that only known `params`
      // are used in combination with a given `pathname`. Since the two will
      // always match for the current route, we can skip runtime checks.
      router.push({ pathname, params }, { locale: nextLocale })
      updateSetting(SettingsItem.LANGUAGE, nextLocale)
    },
    [params, router, updateSetting],
  )

  const handleReligionChange = useCallback((event: SelectChangeEvent) => {
    const selectedReligion = event.target.value
    updateSetting(SettingsItem.RELIGION, selectedReligion)
    if (selectedReligion === SupportedReligion.Christianity) {
      const hiddenFields = ['caste', 'gothra', 'rashi', 'nakshatra', 'mool']
      setPersonalDetails((personalDetails) =>
        personalDetails.map((field) => ({
          ...field,
          isHidden: hiddenFields.includes(field.identifier),
        })),
      )
    } else if (selectedReligion === SupportedReligion.Buddhism) {
      const hiddenFields = ['gothra', 'rashi', 'nakshatra', 'mool', 'kuldevat', 'gan', 'nadi', 'mangalik']
      setPersonalDetails((personalDetails) =>
        personalDetails.map((field) => ({
          ...field,
          isHidden: hiddenFields.includes(field.identifier),
        })),
      )
    } else if (selectedReligion === SupportedReligion.Islam) {
      const hiddenFields = ['gothra', 'rashi', 'nakshatra', 'mool']
      setPersonalDetails((personalDetails) =>
        personalDetails.map((field) => ({
          ...field,
          isHidden: hiddenFields.includes(field.identifier),
        })),
      )
    }
    logger.info('Religion changed', { religion: selectedReligion })
  }, [])

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 6 }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'unset' },
            mb: 2
          }}>
          <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
            <FormControl fullWidth size="small" variant="outlined">
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('BIODATA_LANGUAGE')}
              </Typography>
              <Select id="language-select" value={locale} onChange={handleLanguageChange} variant="outlined">
                {LANGUAGE_OPTIONS.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
            <FormControl fullWidth size="small" variant="outlined">
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('RELIGION')}
              </Typography>
              <Select id="religion-select" value={settings.religion} onChange={handleReligionChange} variant="outlined">
                {RELIGION_DROPDOWN_OPTIONS[locale as LanguageCode].map((religion) => (
                  <MenuItem key={religion.code} value={religion.code}>
                    {religion.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
        <FormSection
          title={sectionTitles.personal}
          fields={personalDetails}
          setFields={setPersonalDetails}
          onTitleChange={(newTitle) => setSectionTitles((prev) => ({ ...prev, personal: newTitle }))}
          isDefaultSection
          isSectionVisible={sectionVisibility.personal}
          onToggleVisibility={() => handleSectionVisibilityToggle('personal')}
        />
        <FormSection
          title={sectionTitles.family}
          fields={familyDetails}
          setFields={setFamilyDetails}
          onTitleChange={(newTitle) => setSectionTitles((prev) => ({ ...prev, family: newTitle }))}
          isDefaultSection
          isSectionVisible={sectionVisibility.family}
          onToggleVisibility={() => handleSectionVisibilityToggle('family')}
        />
        <FormSection
          title={sectionTitles.contact}
          fields={contactDetails}
          setFields={setContactDetails}
          onTitleChange={(newTitle) => setSectionTitles((prev) => ({ ...prev, contact: newTitle }))}
          isDefaultSection
          isSectionVisible={sectionVisibility.contact}
          onToggleVisibility={() => handleSectionVisibilityToggle('contact')}
        />

        {/* Custom Sections */}
        {customSections.map((section) => (
          <FormSection
            key={section.id}
            title={section.title}
            fields={section.fields}
            setFields={handleCustomSectionFieldsChange(section.id)}
            onTitleChange={handleCustomSectionTitleChange(section.id)}
            onDelete={handleCustomSectionDelete(section.id)}
          />
        ))}

        <Stack
          direction="row"
          sx={{
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'unset' },
            mb: 4
          }}>
          <Button variant="outlined" sx={{ width: '100%' }} size="large" onClick={addNewSection} startIcon={<Add />}>
            {t('ADD_NEW_SECTION')}
          </Button>
        </Stack>

        <Stack
          direction="row"
          sx={{
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'unset' }
          }}>
          <Button variant="outlined" sx={{ width: { xs: '100%', sm: '50%' } }} size="large" onClick={generateBiodataMessage}>
            {t('VIEW_BIODATA_DETAILS_BTN')}
          </Button>
          <Button
            variant="contained"
            sx={{
              width: { xs: '100%', sm: '50%' },
            }}
            size="large"
            onClick={handleOpenTemplateView}
          >
            {t('CHOOSE_TEMPLATE_BTN')}
          </Button>
        </Stack>
        {locale !== 'en' && <InputTranslator targetLanguage={locale} />}
      </Container>
      <Drawer
        open={openTemplateView}
        onClose={handleCloseTemplateView}
        anchor="bottom"
        slotProps={{
          paper: {
            sx: {
              height: '100%',
            },
          },
        }}
      >
        <TemplateViewComponent
          personalDetails={personalDetails}
          contactDetails={contactDetails}
          familyDetails={familyDetails}
          customSections={customSections}
          sectionTitles={sectionTitles}
          handleCloseTemplateView={handleCloseTemplateView}
          sectionVisibility={sectionVisibility}
        />
      </Drawer>
      <Dialog open={open} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('MODAL_TITLE')}</DialogTitle>
        <DialogContent>
          <DialogContentText
            dangerouslySetInnerHTML={{
              __html: biodataMessage.replaceAll('\n', '<br>').replace(/\*(.*?)\*/g, '<b>$1</b>'),
            }}
          ></DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleModalClose} variant="outlined">
            {t('MODAL_BUTTON_CLOSE')}
          </Button>
          <Button onClick={copyClickHandler} autoFocus variant="contained">
            {t('MODAL_BUTTON_COPY')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
export default Home
